import { apiClient } from '@/shared/api/client';
import { filenameFromContentDisposition } from '@/shared/lib/contentDisposition';
import { readHttpErrorDetail } from '@/shared/lib/httpErrorDetail';
import { normalizeSearchRegionSelection } from '@/pages/tenders/shared/regions/regionSelection';

export type TenderModuleLimit = {
  enabled?: boolean;
  max_jobs_per_month?: number;
  max_tokens_per_month?: number;
};

export type TenderPlan = {
  id?: string;
  code?: string;
  title?: string;
  description?: string;
  price_cents?: number;
  amount_cents?: number;
  period_months?: number;
  is_default?: boolean;
  active?: boolean;
  modules?: Record<string, TenderModuleLimit>;
  max_tender_amount_rub?: number;
  max_okved_codes?: number;
};

export type TenderPromotion = {
  id?: string;
  code?: string;
  title?: string;
  description?: string;
  module_boosts?: Record<string, TenderModuleLimit>;
};

export type TenderQuotaItem = {
  module_id?: string;
  module_label?: string;
  enabled?: boolean;
  jobs_count?: number;
  tokens_count?: number;
  max_jobs_per_month?: number;
  max_tokens_per_month?: number;
  remaining_jobs?: number;
  usage_percent?: number;
  unlimited?: boolean;
  over_quota?: boolean;
};

export type TenderSubscription = {
  plan_code?: string;
  plan_id?: string;
  plan_title?: string;
  valid_until?: string | null;
  tariff_status?: string;
  status?: string;
  status_label?: string;
};

export type TenderServicePack = {
  code: string;
  title: string;
  description?: string;
  amount_cents: number;
  module_id: string;
  jobs_added: number;
};

export type TenderTariffActivity = {
  searches_total?: number;
  searches_manual?: number;
  searches_scheduled?: number;
  reports_total?: number;
  reports_manual?: number;
  reports_auto?: number;
};

export type TenderTariffAction = 'change' | 'renew' | 'cancel' | 'reactivate';

export type TenderTariffActionResult = {
  ok?: boolean;
  checkout?: boolean;
  plan_id?: string;
};

export type TenderPayment = {
  id?: string;
  amount_cents?: number;
  currency?: string;
  status?: string;
  status_label?: string;
  method?: string;
  note?: string;
  created_at?: string;
  paid_at?: string | null;
  plan_id?: string;
  payment_url?: string;
  order_id?: string;
  tbank_payment_id?: string;
};

export type TenderTariffResponse = {
  usage: Record<string, unknown>;
  client_id: string;
  profile_id?: string;
  profile_usage?: Record<string, unknown>;
  subscription?: TenderSubscription;
  quota?: TenderQuotaItem[];
  period?: string;
  limits_period_ends_at?: string;
  activity?: TenderTariffActivity;
  packs?: TenderServicePack[];
};

export type OnboardingInput = {
  company_name: string;
  inn: string;
  ogrn?: string;
  contact_email?: string;
  contact_phone?: string;
  legal_address?: string;
};

function cabinetPath(path: string): string {
  return `tenders/cabinet/${path.replace(/^\//, '')}`;
}

function parseCabinetQuery(query: string): Record<string, string> | undefined {
  const q = query.trim();
  if (!q) return undefined;
  const params = new URLSearchParams(q.startsWith('?') ? q.slice(1) : q);
  const out = Object.fromEntries(params.entries());
  return Object.keys(out).length ? out : undefined;
}

async function fetchCabinetBlob(path: string, query = '', fallbackName = 'download.bin') {
  const res = await apiClient.get(cabinetPath(path), {
    searchParams: parseCabinetQuery(query),
    timeout: 90_000,
    throwHttpErrors: false,
  });
  if (!res.ok) {
    throw new Error(await readHttpErrorDetail(res));
  }
  const blob = await res.blob();
  const filename = filenameFromContentDisposition(res.headers.get('Content-Disposition'), fallbackName);
  return { blob, filename, contentType: res.headers.get('Content-Type') ?? blob.type };
}

export const tendersApi = {
  health: () =>
    apiClient.get('tenders/health').json<{
      ok: boolean;
      tender_api: boolean;
      company_cabinet: boolean;
      message_gateway?: boolean;
      platform_admin: boolean;
    }>(),

  onboardingStatus: () =>
    apiClient.get('tenders/onboarding/status').json<{
      ready: boolean;
      profile_id?: string;
      company?: {
        company_name?: string;
        inn?: string;
        ogrn?: string;
        contact_email?: string;
        contact_phone?: string;
        legal_address?: string;
      };
    }>(),

  onboardingSubmit: (body: OnboardingInput) =>
    apiClient.post('tenders/onboarding', { json: body }).json<{ ok: boolean; profile_id?: string }>(),

  catalog: () =>
    apiClient
      .get('tenders/catalog', { timeout: 30_000 })
      .json<{
        plans?: { items?: TenderPlan[] };
        promotions?: { items?: TenderPromotion[] } | TenderPromotion[];
      }>(),

  tariff: () => apiClient.get('tenders/tariff').json<TenderTariffResponse>(),

  tariffAction: (body: { action: TenderTariffAction; plan_id?: string }) =>
    apiClient.post('tenders/tariff/actions', { json: body }).json<TenderTariffActionResult>(),

  createPayment: (body: { plan_id: string; amount_cents: number; note?: string; use_bonus?: boolean }) =>
    apiClient.post('tenders/billing/payments', { json: body }).json<{
      payment?: TenderPayment;
      payment_url?: string;
      paid_with_bonus?: boolean;
      bonus_points?: number;
      card_amount_cents?: number;
    }>(),

  payments: () =>
    apiClient.get('tenders/billing/payments').json<{ items?: TenderPayment[] }>(),

  cabinetGet: <T = Record<string, unknown>>(path: string, searchParams?: Record<string, string | number | boolean | undefined>) => {
    const clean: Record<string, string | number | boolean> = {};
    if (searchParams) {
      for (const [k, v] of Object.entries(searchParams)) {
        if (v !== undefined && v !== '') clean[k] = v;
      }
    }
    return apiClient
      .get(cabinetPath(path), { searchParams: Object.keys(clean).length ? clean : undefined })
      .json<T>();
  },

  cabinetPost: <T = Record<string, unknown>>(path: string, json: unknown) =>
    apiClient.post(cabinetPath(path), { json }).json<T>(),

  cabinetPut: <T = Record<string, unknown>>(path: string, json: unknown) =>
    apiClient.put(cabinetPath(path), { json }).json<T>(),

  cabinetPatch: <T = Record<string, unknown>>(path: string, json: unknown) =>
    apiClient.patch(cabinetPath(path), { json }).json<T>(),

  cabinetDelete: (path: string) => apiClient.delete(cabinetPath(path)),

  messagesChannels: () => apiClient.get('tenders/channels').json<{ channels?: Array<Record<string, unknown>> }>(),
  messagesList: () => apiClient.get('tenders/messages').json<{ items?: Array<Record<string, unknown>> }>(),
  messagesGet: (id: string) =>
    apiClient.get(`tenders/messages/${id}`).json<{ message?: Record<string, unknown>; deliveries?: Array<Record<string, unknown>> }>(),
  messagesSubmit: (json: Record<string, unknown>) =>
    apiClient.post('tenders/messages', { json }).json<Record<string, unknown>>(),
  messagesRetry: (id: string) =>
    apiClient.post(`tenders/messages/${id}/retry`).json<Record<string, unknown>>(),

  /** Fetch binary without triggering download (for in-app preview). */
  cabinetBlob: fetchCabinetBlob,

  /** Binary download (PDF/DOCX) via cabinet proxy; preserves Content-Disposition when possible. */
  cabinetDownload: async (path: string, query = '', fallbackName = 'download.bin') => {
    const { blob, filename } = await fetchCabinetBlob(path, query, fallbackName);
    const href = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = href;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(href);
  },

  searchSubmit: (query: string, topK = 50, regions: string[] = [], catalogCount = 0) =>
    apiClient
      .post('tenders/search/submit', {
        json: {
          query_text: query,
          top_k: topK,
          region_texts: normalizeSearchRegionSelection(regions, catalogCount),
        },
        timeout: 90_000,
      })
      .json<{ search_id?: string; id?: string }>(),

  searchList: () =>
    apiClient.get('tenders/search').json<{ items?: Record<string, unknown>[] }>(),

  searchGet: (id: string) =>
    apiClient.get(`tenders/search/${id}`).json<Record<string, unknown>>(),

  /** Company-cabinet search funnel (configured / in work / completed by kind). */
  searchPipelineStatus: (refresh = false) =>
    tendersApi.cabinetGet<SearchPipelineStatusResponse>(
      'api/search-pipeline-status',
      refresh ? { refresh: 1 } : undefined,
    ),
};

/** Mirrors company-cabinet GET /api/search-pipeline-status. */
export type SearchPipelineStatusResponse = {
  ok?: boolean;
  error?: string;
  refreshed_at?: string;
  next_refresh_sec?: number;
  period?: string;
  status?: { pipeline?: string };
  configured?: {
    saved_searches?: number;
    schedules_active?: number;
    schedules_total?: number;
  };
  kinds?: {
    manual?: { total?: number; completed?: number; in_work?: number } | number;
    schedule?: { total?: number; completed?: number; in_work?: number } | number;
  };
  chain?: {
    configured?: number;
    searches?: number;
    found?: number;
    in_work?: number;
    reports?: number;
  };
  live?: {
    searches_in_work?: number;
    reports_in_work?: number;
    manual_in_work?: number;
    schedule_in_work?: number;
  };
  window_1h?: {
    searches?: number;
    found?: number;
    reports?: number;
  };
};
